import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnInit,
    signal,
    ViewChild,
    WritableSignal,
} from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import {
    CustAttrType,
    CustAttrTypeCodes,
    MasterDefData,
    MasterDefinitionService,
    MasterDefType,
} from "../../services/master.definitions/master.definition.service";
import { Table } from "../s25-table/Table";
import { S25EditableTextComponent } from "../s25-editable/s25-editable-text/s25.editable.text.component";
import { S25CheckboxComponent } from "../s25-checkbox/s25.checkbox.component";
import {
    PolicyInputData,
    S25NotificationPolicyComponent,
} from "../s25-notification-policy/s25.notification.policy.component";
import { S25MdTagMultiSelectComponent } from "./s25.md.tag.picker.component";
import { S25MasterDefinitionUsageReportComponent } from "./s25.master.defs.usage.report.component";
import { GenericTableButtonComponent } from "../s25-table/generics/generic.table.button.component";
import { Bind } from "../../decorators/bind.decorator";
import { S25Util } from "../../util/s25-util";
import { S25ModalComponent } from "../s25-modal/s25.modal.component";
import { PreferenceService } from "../../services/preference.service";
import { jSith } from "../../util/jquery-replacement";
import { FlsService } from "../../services/fls.service";
import { S25TableComponent } from "../s25-table/s25.table.component";
import { UserprefService } from "../../services/userpref.service";
import { S25LoadingApi } from "../s25-loading/loading.api";
import { MasterDef } from "../../pojo/MasterDefs";
import { MultiselectModelI } from "../s25-multiselect/s25.multiselect.component";
import { MasterDefinitionTagsService } from "../../services/master.definitions/master.definition.tags.service";
import { S25Const } from "../../util/s25-const";
import { S25ButtonComponent } from "../../standalone/s25.button.component";
import { S25CustomAttributeOptionsComponent } from "../s25-custom-attribute/s25.custom.attribute.options.component";
import { CustomAttributeService } from "../../services/custom.attribute.service";
import { OrganizationRatings } from "../../pojo/OrganizationI";
import { GenericTableSimpleSelectComponent } from "../s25-table/generics/generic.table.simple.select.component";
import { S25RateGroupDropdownComponent } from "../s25-dropdown/single-select/s25.rate.group.dropdown.component";
import { S25ItemI } from "../../pojo/S25ItemI";
import { S25ShiftSelectableCheckboxComponent } from "../s25-checkbox/shift.selectable.checkbox.component";
import { TableHeaderSelectAllComponent } from "../s25-table/generics/table.header.select.all.component";
import { SetItem } from "../../pojo/Util";
import { Debounce } from "../../decorators/debounce.decorator";
import { MasterDefinitionUtil } from "../../util/master.definition.util";
import { CustomAttributes } from "../../pojo/CustomAttributes";
import { S25UsageComponent } from "../../standalone/s25.usage.component";
import { TablePopoverButtonComponent } from "../s25-table/generics/table.popover.button.component";

@TypeManagerDecorator("s25-ng-master-definition")
@Component({
    selector: "s25-ng-master-definition",
    template: ` @if (init()) {
        <div class="master-defs--wrapper">
            <h2 class="c-margin-bottom--single">{{ header }}</h2>

            <div class="master-defs--action-wrapper">
                @if (type !== "event_type") {
                    @if (data.idProp === "attribute_id") {
                        <label>
                            Type:
                            <select class="cn-form__control" [(ngModel)]="chosenCustAttrType">
                                @for (type of MasterDefinitionService.custAttrTypes; track type.typeCode) {
                                    <option [ngValue]="type">
                                        {{ type.itemName }}
                                    </option>
                                }
                            </select>
                        </label>
                    }
                    <button
                        class="aw-button aw-button--primary master-defs-add-new"
                        (click)="create()"
                        [disabled]="isCreating() || (data.idProp === 'attribute_id' && !chosenCustAttrType)"
                    >
                        {{ isCreating() ? "" : "Create New" }}
                        <s25-loading-inline [model]="{}"></s25-loading-inline>
                    </button>
                }
            </div>

            @if (abridgedWarningText()) {
                <div class="rose-form--error"></div>
                <div class="rose-form--organization-rating-info c-margin-top--half c-margin-bottom--half">
                    <div class="rose-form--error-text">
                        <strong>No items are selected for the Abridged list.</strong><br />
                        {{ abridgedWarningText() }}
                    </div>
                </div>
            }

            <s25-ng-table
                [dataSource]="tableConfig"
                [rowSortable]="true"
                [columnSortable]="true"
                [sortOrderPreference]="type"
                [hasRefresh]="true"
                [align]="'center'"
                [hasFilter]="true"
            ></s25-ng-table>

            <s25-ng-modal #forceDeleteModal [title]="'Confirm Deletion'" [type]="'deny'" [size]="'xs'">
                Are you sure that you want to delete this master definition and remove all references to it?
            </s25-ng-modal>

            <s25-ng-modal #npModal [title]="'Notification Policy'" [size]="'xs'">
                <ng-template #s25ModalBody>
                    <s25-ng-notification-policy [data]="policyData"></s25-ng-notification-policy>
                </ng-template>
                <ng-template #s25ModalFooter>
                    <button class="aw-button aw-button--danger--outline" (click)="deletePolicy()">Delete</button>
                    <button class="aw-button aw-button--primary" (click)="savePolicy()">Save</button>
                </ng-template>
            </s25-ng-modal>

            <s25-ng-modal #discreteModal [title]="'Discrete Options'" [size]="'xs'">
                <ng-template #s25ModalBody>
                    <s25-ng-custom-attribute-options
                        [attrId]="custAttrId"
                        [inTable]="true"
                    ></s25-ng-custom-attribute-options>
                </ng-template>
                <ng-template #s25ModalFooter>
                    <button class="aw-button aw-button--primary" (click)="saveDscrtOptions()">Save</button>
                </ng-template>
            </s25-ng-modal>
        </div>
    }`,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25MasterDefinitionComponent implements OnInit {
    @Input() type: MasterDefType;

    @ViewChild("forceDeleteModal") forceDeleteModal: S25ModalComponent;
    @ViewChild("npModal") npModal: S25ModalComponent;
    @ViewChild("discreteModal") discreteModal: S25ModalComponent;
    @ViewChild(S25NotificationPolicyComponent) notificationPolicyComp: S25NotificationPolicyComponent;
    @ViewChild(S25TableComponent) tableComp: S25TableComponent;
    @ViewChild(S25CustomAttributeOptionsComponent) customAttributeOptionsComp: S25CustomAttributeOptionsComponent;

    init = signal<boolean>(false);
    abridgedWarningText = signal<string>("");
    isCreating = signal<boolean>(false);
    prefMap: { [key: MasterDefType]: { [key: number]: boolean | string } } = {};
    data: MasterDefData;
    tableConfig: Table.DataSource;
    custAttrId: number;
    policyData: PolicyInputData;
    canEditNP: boolean;
    groupId: number;
    chosenCustAttrType: CustAttrType<CustAttrTypeCodes>;
    allSelected: Partial<Record<SelectAllColumn, WritableSignal<boolean>>> = Object.fromEntries(
        Array.from(selectAllColumnsSet.values()).map((col) => [col, signal(false)]),
    );

    get header() {
        const header = this.type
            .split("_")
            .map((char) => `${char[0].toUpperCase()}${char.substring(1).toLowerCase()}`)
            .join(" ");

        if (header.at(-1) === "y") return header.slice(0, -1) + "ies";

        return header + "s";
    }

    constructor(
        private elementRef: ElementRef,
        private cd: ChangeDetectorRef,
    ) {}

    async ngOnInit() {
        this.data = MasterDefinitionService.masterDefsConst[this.type];

        const [groupId, fls] = await Promise.all([
            UserprefService.getGroupId(),
            this.type.indexOf("requirement") > -1 && FlsService.getFls(),
        ]);

        this.groupId = groupId;
        if (fls) this.canEditNP = fls.EVENT_NOTIFY === "F";

        this.tableConfig = {
            type: "unpaginated",
            dataSource: this.getRows,
            columns: this.setTableColumns(),
        };

        this.init.set(true);
    }

    @Bind
    async getRows(query: Table.UnpaginatedQuery) {
        if (query.forceRefresh) {
            if (!!this.data.prefs) PreferenceService.invalidatePreferences(this.data.prefs.map((pref) => pref.name));
        }

        let [rows, prefs] = await Promise.all([
            MasterDefinitionService.get(this.type),
            !!this.data.prefs && PreferenceService.getPreferences(this.data.prefs.map((pref) => pref.name)),
        ]);

        if (this.type === "event_type") rows = rows.filter((i: MasterDef) => i.type_id > 0); // event type filter negative ids

        prefs &&
            jSith.forEach(prefs, (prefName, obj) => {
                this.prefMap[prefName] = {};
                if (prefName === "config_org_ratings") {
                    const ratings: OrganizationRatings[] = S25Util.array.forceArray(
                        (obj.value && S25Util.propertyGet(obj.value, "rating")) ?? [],
                    );

                    ratings.forEach((rating) => {
                        if (rating.rating_id) {
                            this.prefMap[prefName][parseInt(rating.rating_id)] = rating.standing ?? "allow";
                        }
                    });
                } else {
                    const prefIds =
                        S25Util.toStr(obj?.value).split(",").filter(S25Util.isTruthy).map(S25Util.toInt) ?? [];

                    if (prefName.indexOf("ev_cust") > -1) {
                        if (
                            prefs?.config_ev_cust_atrb_details?.value === "" &&
                            prefs?.config_ev_cust_atrb_wizard?.value === ""
                        )
                            this.setWarningText(this.data.prefs[0].name);
                    } else {
                        if (!prefIds.length) {
                            this.setWarningText(this.data.prefs[0].name);
                        }
                    }

                    prefIds.forEach((itemId) => {
                        this.prefMap[prefName][itemId] ??= true;
                    });
                }
            });

        this.allSelected.active.set(rows.every((row: MasterDef) => !!row.defn_state));
        this.allSelected.schedule25.set(rows.every((row: MasterDef) => !!row.s25_feature_number));
        this.allSelected.allowQty.set(rows.every((row: MasterDef) => row.stock_count >= 0));
        this.allSelected.allowComment.set(rows.every((row: MasterDef) => !!row.allow_comment));
        for (const pref of this.data.prefs || []) {
            if (!!pref.selectOptions) continue;
            this.allSelected[pref.id as SelectAllColumn].set(
                rows.every((row: MasterDef) => !!this.prefMap[pref.name][row[this.data.idProp]]),
            );
        }

        return {
            rows: rows?.map(this.mapToRows),
        };
    }

    @Bind
    mapToRows(row: MasterDef, i: number): Table.Row {
        const {
            idProp,
            nameProp,
            prefs,
            hasAllowQty,
            hasAllowComment,
            hasNotification,
            hasTags,
            hasType,
            hasDiscrete,
            hasMultiVal,
            hasRateGroup,
            hasSchedule25,
            nameLen,
            blockNameEditIds,
        } = this.data;
        const id = row[idProp];
        const isTextType = row.attribute_type === "S";
        const rowName = row[nameProp]?.toString();
        const blockNameEdit = blockNameEditIds?.includes(id);
        if (this.prefMap.config_org_ratings && !this.prefMap.config_org_ratings[id]) {
            this.prefMap.config_org_ratings[id] = "allow";
        }
        const isUrl = MasterDefinitionUtil.isUrl(
            this.type as CustomAttributes.UrlAttributeType,
            row.attribute_type,
            row.attribute_id,
        );
        return {
            id: id,
            name: rowName,
            cells: {
                id: { text: id },
                name: {
                    textValue: rowName,
                    component: S25EditableTextComponent,
                    inputs: {
                        val: rowName,
                        readOnly: !!blockNameEdit,
                        hasCommit: true,
                        hasCommitButton: true,
                        hasCancelButton: true,
                        min: 1,
                        max: nameLen,
                        customValidation: (newVal: string) => this.onNameChange(newVal, id),
                    },
                },
                active: {
                    component: S25ShiftSelectableCheckboxComponent,
                    inputs: {
                        modelValue: row.defn_state,
                        ariaLabel: `Set ${rowName} Active`,
                        index: i,
                        group: `${this.type}-active`,
                        noLabel: true,
                    },
                    outputs: {
                        modelValueChange: (checked: boolean) => {
                            this.onCheckboxChange("active", checked);
                            this.updateSetting("setActive", id, checked);
                        },
                    },
                },
                ...prefs?.reduce((acc: { [key: string]: Table.Cell }, pref) => {
                    if (!!pref.selectOptions) {
                        acc[pref.id] = {
                            component: GenericTableSimpleSelectComponent,
                            inputs: {
                                modelValue: this.prefMap[pref.name][id],
                                options: pref.selectOptions,
                            },
                            outputs: {
                                modelValueChange: (checked: boolean | string) => {
                                    this.updatePreference(pref.name, id, checked);
                                },
                            },
                        };
                    } else {
                        acc[pref.id] = {
                            component: S25ShiftSelectableCheckboxComponent,
                            inputs: {
                                modelValue: this.prefMap[pref.name][id],
                                ariaLabel: `Set ${pref.header} for ${rowName}`,
                                index: i,
                                group: `${this.type}-${pref.id}`,
                                noLabel: true,
                            },
                            outputs: {
                                modelValueChange: (checked: boolean | string) => {
                                    this.onCheckboxChange(pref.id as SelectAllColumn, checked as boolean);
                                    this.updatePreference(pref.name, id, checked);
                                },
                            },
                        };
                    }
                    return acc;
                }, {}),
                ...(hasRateGroup && {
                    rateGroup: {
                        component: S25RateGroupDropdownComponent,
                        inputs: {
                            chosen: {
                                itemName:
                                    typeof row.rate_group?.rate_group_name === "string"
                                        ? row.rate_group.rate_group_name
                                        : null,
                                itemId: !isNaN(row.rate_group?.rate_group_id) ? row.rate_group.rate_group_id : null,
                            },
                            staticItems: [{ itemId: "", itemName: "None" }],
                            ariaLabel: `Select Default Rate Group for ${rowName}`,
                        },
                        outputs: {
                            chosenChange: (selectedRateGroup: S25ItemI) =>
                                this.updateSetting("setRateGroup", id, selectedRateGroup),
                        },
                    },
                }),
                ...(hasSchedule25 && {
                    schedule25: {
                        component: S25ShiftSelectableCheckboxComponent,
                        inputs: {
                            modelValue: row.s25_feature_number,
                            ariaLabel: `Enable Schedule25 for ${rowName}`,
                            index: i,
                            group: `${this.type}-schedule25`,
                            noLabel: true,
                        },
                        outputs: {
                            modelValueChange: (checked: boolean) => {
                                this.onCheckboxChange("schedule25", checked);
                                this.updateSetting("setSchedule25", id, checked);
                            },
                        },
                    },
                }),
                ...(hasMultiVal && {
                    multiVal: isTextType
                        ? {
                              component: S25CheckboxComponent,
                              inputs: {
                                  modelValue: row.multi_val === "T",
                                  ariaLabel: `Enable Multi-value for ${rowName}`,
                                  blockChange: () => this.checkCanSelectMultiVals(id),
                              },
                              outputs: {
                                  modelValueChange: (isChecked: boolean) =>
                                      this.updateSetting("setMultiVal", id, isChecked),
                              },
                          }
                        : { text: "" },
                }),
                ...(hasType && {
                    type: { text: !isUrl ? S25Const.custAtrbType2Name[row.attribute_type] : "URL" },
                }),
                ...(hasDiscrete && {
                    discrete: isTextType
                        ? {
                              component: S25ButtonComponent,
                              inputs: { type: "outline", buttonText: "Show" },
                              outputs: {
                                  click: async () => {
                                      this.custAttrId = id;
                                      await this.discreteModal.open();
                                  },
                              },
                          }
                        : { text: "" },
                }),
                ...(hasAllowQty && {
                    allowQty: {
                        component: S25ShiftSelectableCheckboxComponent,
                        inputs: {
                            modelValue: row.stock_count >= 0,
                            ariaLabel: `Enable Allow Quantity for ${rowName}`,
                            index: i,
                            group: `${this.type}-allowQty`,
                            noLabel: true,
                        },
                        outputs: {
                            modelValueChange: (isChecked: boolean) => {
                                this.onCheckboxChange("allowQty", isChecked);
                                this.updateSetting("setStockCount", id, isChecked);
                            },
                        },
                    },
                }),
                ...(hasAllowComment && {
                    allowComment: {
                        component: S25ShiftSelectableCheckboxComponent,
                        inputs: {
                            modelValue: !!row.allow_comment,
                            ariaLabel: `Enable Allow Comment for ${rowName}`,
                            index: i,
                            group: `${this.type}-allowComment`,
                            noLabel: true,
                        },
                        outputs: {
                            modelValueChange: (isChecked) => {
                                this.onCheckboxChange("allowComment", isChecked);
                                this.updateSetting("setReqComment", id, isChecked);
                            },
                        },
                    } satisfies Table.Cell<S25ShiftSelectableCheckboxComponent>,
                }),
                ...(hasNotification &&
                    this.canEditNP && {
                        notification: {
                            component: S25NotificationPolicyComponent,
                        },
                    }),
                ...(hasTags && {
                    tags: {
                        component: S25MdTagMultiSelectComponent,
                        inputs: {
                            selectedItems: S25Util.array
                                .forceArray(row.tags)
                                .map((tag) => ({ itemId: tag.tag_id, itemName: tag.tag_name })),
                            selectBean: { hasCommit: true },
                        },
                        outputs: {
                            selectedItemsChange: (selectBean) => this.onTagsUpdate(selectBean, id),
                        },
                    },
                }),
                usage: {
                    inputs: {
                        label: "Usage",
                        cell: {
                            component: S25UsageComponent,
                            inputs: { service: MasterDefinitionUtil.usageService.bind(this, this.type, id) },
                        },
                    },
                },
                report: {
                    component: S25MasterDefinitionUsageReportComponent,
                    inputs: { type: this.type, itemId: id },
                },
                delete: {
                    inputs: { label: "Delete", type: "danger--outline", disabled: id < 0 },
                },
                ...(this.groupId === -1 && {
                    force: {
                        inputs: { label: "Force Delete", type: "danger--outline", disabled: id < 0 },
                    },
                }),
            },
        };
    }

    setTableColumns(): Table.Column[] {
        const {
            prefs,
            hasType,
            hasDiscrete,
            hasAllowQty,
            hasAllowComment,
            hasNotification,
            hasRateGroup,
            hasSchedule25,
            hasTags,
            hasMultiVal,
        } = this.data;
        return [
            { id: "id", header: "ID", width: "min-content", sortable: false },
            { id: "name", header: "Name" },
            {
                id: "active",
                header: "Active",
                content: {
                    component: TableHeaderSelectAllComponent,
                    inputs: { label: "Active", allSelected: this.allSelected.active },
                    outputs: { toggle: this.selectAll.bind(this, "active") },
                },
                align: "center",
                maxWidth: 75,
                sortable: false,
            },
            ...(prefs
                ? prefs.map((pref) => ({
                      id: pref.id,
                      header: pref.header,
                      content: {
                          component: pref.selectOptions ? null : TableHeaderSelectAllComponent,
                          inputs: { label: pref.header, allSelected: this.allSelected[pref.id as SelectAllColumn] },
                          outputs: { toggle: this.selectAll.bind(this, pref.id) },
                      },
                      align: "center",
                      maxWidth: 75,
                      sortable: false,
                  }))
                : []),
            ...(hasRateGroup ? [{ id: "rateGroup", header: "Default Rate Group", sortable: false }] : []),
            ...(hasSchedule25
                ? [
                      {
                          id: "schedule25",
                          header: "Schedule25",
                          sortable: false,
                          content: {
                              component: TableHeaderSelectAllComponent,
                              inputs: { label: "Schedule25", allSelected: this.allSelected.schedule25 },
                              outputs: { toggle: this.selectAll.bind(this, "schedule25") },
                          },
                      },
                  ]
                : []),
            ...(hasMultiVal ? [{ id: "multiVal", header: "Multi-valued", width: 66, sortable: false }] : []),
            ...(hasType
                ? [
                      {
                          id: "type",
                          header: "Type",
                          sortable: false,
                      },
                  ]
                : []),
            ...(hasDiscrete
                ? [
                      {
                          id: "discrete",
                          header: "Discrete Options",
                          width: 75,
                          sortable: false,
                      },
                  ]
                : []),
            ...(hasAllowQty
                ? [
                      {
                          id: "allowQty",
                          header: "Allow Quantity",
                          cells: { component: S25CheckboxComponent },
                          content: {
                              component: TableHeaderSelectAllComponent,
                              inputs: { label: "Allow Quantity", allSelected: this.allSelected.allowQty },
                              outputs: { toggle: this.selectAll.bind(this, "allowQty") },
                          },
                          align: "center",
                          maxWidth: 75,
                          sortable: false,
                      },
                  ]
                : []),
            ...(hasAllowComment
                ? [
                      {
                          id: "allowComment",
                          header: "Allow Comment",
                          cells: { component: S25CheckboxComponent },
                          content: {
                              component: TableHeaderSelectAllComponent,
                              inputs: { label: "Allow Comment", allSelected: this.allSelected.allowComment },
                              outputs: { toggle: this.selectAll.bind(this, "allowComment") },
                          },
                          align: "center",
                          maxWidth: 75,
                          sortable: false,
                      },
                  ]
                : []),
            ...(hasNotification && this.canEditNP
                ? [
                      GenericTableButtonComponent.Column(
                          "Show",
                          this.initNotificationModal,
                          "outline",
                          75,
                          null,
                          "Notification Policy",
                      ),
                  ]
                : []),
            ...(hasTags
                ? [
                      {
                          id: "tags",
                          header: "Tags",
                          width: 120,
                          cells: { component: S25MdTagMultiSelectComponent },
                          sortable: false,
                      },
                  ]
                : []),
            {
                id: "usage",
                header: "Usage",
                width: 75,
                cells: { component: TablePopoverButtonComponent },
                sortable: false,
            },
            {
                id: "report",
                header: "Report",
                width: 75,
                cells: { component: S25MasterDefinitionUsageReportComponent },
                sortable: false,
            },
            GenericTableButtonComponent.Column("Delete", this.delete, "danger--outline", 75),
            ...(this.groupId === -1
                ? [
                      GenericTableButtonComponent.Column(
                          "force",
                          this.forceDelete,
                          "danger--outline",
                          75,
                          null,
                          "Force Delete",
                      ),
                  ]
                : []),
        ];
    }

    @Bind
    async delete(row: Table.Row, instance: GenericTableButtonComponent) {
        instance.isLoading = true;
        try {
            await MasterDefinitionService.delete(this.type, row.id as number);
            !!this.data.prefs && (await this.handlePrefCleanup(row.id as number));
            return this.tableComp.refresh();
        } catch (e) {
            if (e.data?.results?.error?.proc_error?.includes("is in use")) {
                alert("This item is in use and cannot be deleted. View the usage report for more information.");
            } else {
                S25Util.showError(e);
            }
            instance.isLoading = false;
        }
    }

    @Bind
    async forceDelete(row: Table.Row, instance: GenericTableButtonComponent) {
        instance.isLoading = true;
        const yes = await this.forceDeleteModal.open();

        if (yes) {
            try {
                await MasterDefinitionService.forceDelete(this.type, row.id as number);
                !!this.data.prefs && (await this.handlePrefCleanup(row.id as number));

                return this.tableComp.refresh();
            } catch (e) {
                S25Util.showError(e);
                instance.isLoading = false;
            }
        } else {
            instance.isLoading = false;
        }
    }

    @Bind
    async handlePrefCleanup(id: number) {
        let promiseArr = [];
        for (let pref in this.prefMap) {
            if (this.prefMap[pref][id]) {
                promiseArr.push(this.updatePreference(pref, id, false));
            }
        }

        promiseArr.length > 0 && (await Promise.all(promiseArr));
    }

    setWarningText(prefName: string) {
        let warningText = "All items will be visible, even to users with the Group Administration tool permission, ";

        switch (true) {
            case prefName.indexOf("sp_") > -1:
                warningText +=
                    'Basic > "15.0 View All Location Master Definitions (Not Just Abridged List)" set to “No."';
                break;
            case prefName.indexOf("rs_") > -1:
                warningText +=
                    'Basic > "17.0 View All Resource Master Definitions (Not Just Abridged List)" set to “No.”';
                break;
            case prefName.indexOf("org_") > -1:
                warningText +=
                    'Basic > "19.0 View All Organization Master Definitions (Not Just Abridged List)" set to “No.”';
                break;
            case prefName.indexOf("ev_") > -1:
                warningText += 'Basic > "13.0 View All Event Master Definitions (Not Just Abridged List)" set to “No.”';
                break;
            default:
                warningText = "";
        }

        this.abridgedWarningText.set(warningText);
        this.cd.detectChanges();
    }

    @Bind
    async initNotificationModal(row: Table.Row) {
        this.policyData = {
            itemId: row.id as number,
            type: this.type,
            canEdit: true,
            inModal: true,
        };

        await this.npModal.open();
    }

    async savePolicy() {
        const resp = await this.notificationPolicyComp?.save();
        if (resp?.results?.info?.msg_id === "EV_I_SAVED") return this.npModal.close();
    }

    async deletePolicy() {
        const resp = await this.notificationPolicyComp?.delete();
        if (resp?.results?.info?.msg_id === "NP_I_DELETED") return this.npModal.close();
    }

    async create() {
        const buttonEl = this.elementRef.nativeElement.querySelector(".master-defs-add-new");
        S25LoadingApi.init(buttonEl);
        this.isCreating.set(true);

        let params = null;
        if (this.type.indexOf("requirement") > -1) {
            params = {
                requirement_type: this.type === "other_requirement" ? 6 : 7,
                stock_count: this.type === "other_requirement" ? 0 : -1,
                allow_comment: 1,
            };
        } else if (this.data.idProp === "attribute_id") {
            params = {
                attribute_type: this.chosenCustAttrType.typeCode,
                attribute_type_name: this.chosenCustAttrType.itemName,
            };
        }

        try {
            await MasterDefinitionService.createNew(this.type, params);
            this.chosenCustAttrType = null;
            await this.tableComp.refresh();
        } catch (e) {
            console.error(e);
        }

        S25LoadingApi.destroy(buttonEl);
        this.isCreating.set(false);
    }

    async updateSetting(prop: string, id: number, updatedVal: boolean | S25ItemI) {
        try {
            await (MasterDefinitionService as any)[prop](this.type, id, updatedVal);
        } catch (e) {
            S25Util.showError(e);
        }
    }

    async updateSettingBulk(prop: string, ids: number[], updatedVal: boolean | S25ItemI) {
        try {
            await (MasterDefinitionService as any)[prop](this.type, ids, updatedVal);
        } catch (e) {
            S25Util.showError(e);
        }
    }

    updatePrefMap(prefName: string, actionId: number, updateValue: boolean | string) {
        if (updateValue) {
            this.prefMap[prefName][actionId] = updateValue;
        } else if (this.prefMap[prefName][actionId]) {
            delete this.prefMap[prefName][actionId];
        }
    }

    @Debounce(250)
    async savePreference(prefName: string) {
        let newValue;
        if (prefName === "config_org_ratings") {
            newValue = `<ratings>${Object.entries(this.prefMap[prefName])
                ?.map(([ratingId, standing]) => `<rating rating_id="${ratingId}" standing="${standing}"></rating>`)
                ?.join("")}</ratings>`;
        } else {
            newValue = Object.keys(this.prefMap[prefName])?.join(",");
        }
        try {
            await PreferenceService.setPreference(prefName, newValue, "S");

            if (prefName.includes("ev_cust_atrb")) {
                const eventFormValue = Object.keys(this.prefMap?.config_ev_cust_atrb_details).join(",");
                const eventDetailValue = Object.keys(this.prefMap?.config_ev_cust_atrb_wizard).join(",");
                if (!eventFormValue && !eventDetailValue) {
                    this.setWarningText(prefName);
                } else {
                    this.abridgedWarningText.set("");
                }
            } else if (!newValue) {
                this.setWarningText(prefName);
            } else {
                this.abridgedWarningText.set("");
            }
        } catch (e) {
            S25Util.showError(e);
        }
    }

    async updatePreference(prefName: string, actionId: number, updateValue: boolean | string) {
        this.updatePrefMap(prefName, actionId, updateValue);
        await this.savePreference(prefName);
    }

    @Bind
    onTagsUpdate(tagsModel: MultiselectModelI, id: number) {
        const added = tagsModel.addedItems?.map((items) => items.itemId);
        const removed = tagsModel.removedItems?.map((items) => items.itemId);

        if (!added?.length && !removed?.length) return;

        return MasterDefinitionTagsService.editTagsOnObject(
            this.data.tagProp,
            null,
            id,
            added as number[],
            removed as number[],
        );
    }

    @Bind
    async onNameChange(newName: string, id: number) {
        try {
            const row = this.tableComp?.rows?.find((row) => row.id === id);
            if (newName === row?.cells?.name?.textValue) return true; //no change
            await MasterDefinitionService.setName(this.type, id, newName);
            //Edit value of a cell in the table from the row with id
            if (row) {
                row.name = newName;
                row.cells.name.textValue = newName; // Update the cell value in the table
            }
            return true;
        } catch (e) {
            if (e.error?.results?.error?.msg_id === "ML_E_STORE") {
                return "This name is already in use";
            }
        }
    }

    async saveDscrtOptions() {
        const resp = await this.customAttributeOptionsComp?.save();
        if (S25Util.isDefined(resp?.root)) return this.discreteModal.close();
    }

    @Bind
    async checkCanSelectMultiVals(id: number) {
        const opts = await CustomAttributeService.getDiscreteOptions(id);

        if (!opts?.length) {
            alert("Please create discrete options first");
            return true;
        }

        return false;
    }

    onCheckboxChange(column: SelectAllColumn, checked: boolean) {
        this.allSelected[column].set(checked ? this.areAllSelected(column) : false);
    }

    areAllSelected(column: SelectAllColumn) {
        return this.tableComp.rows.every((row) => !!row.cells[column].instance.modelValue);
    }

    async selectAll(column: SelectAllColumn, checked: boolean) {
        // This updates checkboxes without calling services
        for (const row of this.tableComp.rows) {
            const instance = row.cells[column].instance;
            if (!instance) continue;
            instance.modelValue = checked;
            instance.cd.markForCheck();
        }
        this.allSelected[column].set(checked);

        //Save the changes
        const ids = this.tableComp.rows
            .filter((row) => row.cells[column].instance.modelValue === checked)
            .map((row) => +row.id);

        switch (column) {
            //preferences
            case "abridged":
            case "evForm":
            case "evDetails":
                const pref = this.data.prefs.find((col) => col.id === column);
                const prefName = pref.name;
                this.tableComp.rows.forEach((row) =>
                    this.updatePrefMap(prefName, +row.id, row.cells[column].instance.modelValue),
                );
                await this.savePreference(prefName);
                break;
            case "active":
                await this.updateSettingBulk("setActive", ids, checked);
                break;
            case "schedule25":
                await this.updateSettingBulk("setSchedule25", ids, checked);
                break;
            case "allowQty":
                await this.updateSettingBulk("setStockCount", ids, checked);
                break;
            case "allowComment":
                await this.updateSettingBulk("setReqComment", ids, checked);
                break;
        }
    }

    protected readonly MasterDefinitionService = MasterDefinitionService;
}

const selectAllColumnsSet = new Set([
    "active",
    "abridged",
    "evDetails",
    "evForm",
    "schedule25",
    "allowQty",
    "allowComment",
] as const);
type SelectAllColumn = SetItem<typeof selectAllColumnsSet>;
